import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { NOT_FOUND_ILLUSTRATION } from "@/lib/event-cover";
import { Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { captureException } from "@/lib/sentry";

function WhatsAppIcon({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    captureException(error, { componentStack: errorInfo.componentStack });
    this.setState({ error, errorInfo });
  }

  getDetailedErrorPayload = () => {
    return {
      message: this.state.error?.message || "Erro inesperado",
      stack: this.state.error?.stack || "",
      componentStack: this.state.errorInfo?.componentStack || "",
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      timestamp: new Date().toISOString(),
    };
  };

  handleCopyError = () => {
    const errorDetails = this.getDetailedErrorPayload();
    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
    toast.success("Detalhes do erro copiados para a área de transferência!");
  };

  handleContactSupport = () => {
    const currentUrl = typeof window !== "undefined" ? window.location.href : "";
    const errorMessage = this.state.error?.message || "Erro inesperado";
    const horaFormatada = new Date().toLocaleString("pt-BR");
    
    // Obter stack limpo e relevante (até 7 linhas) para formatação no WhatsApp
    const rawStack = this.state.error?.stack || this.state.errorInfo?.componentStack || "";
    const cleanStackLines = rawStack
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0 && !line.includes("node_modules"))
      .slice(0, 6);
    
    const stackSnippet = cleanStackLines.length > 0 
      ? cleanStackLines.join("\n") 
      : rawStack.split("\n").slice(0, 4).join("\n");

    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isMobile = /Android|iPhone|iPad|Mobile/i.test(userAgent);
    const platform = isMobile ? "Dispositivo Móvel" : "Computador / Desktop";

    const textParts = [
      "🚨 *Chamado de Suporte - Erro no Site TicketHall*",
      "",
      `📍 *Rota:* ${currentUrl}`,
      `⚠️ *Mensagem do Erro:* ${errorMessage}`,
      `🕒 *Data/Hora:* ${horaFormatada}`,
      `💻 *Plataforma:* ${platform}`,
      "",
      "🔧 *Detalhes Técnicos (Stack Trace):*",
      "```",
      stackSnippet || "Nenhum stack trace registrado.",
      "```",
      "",
      "Poderiam me ajudar a solucionar este problema, por favor?"
    ];

    const fullMessage = textParts.join("\n");

    // Também copia para o clipboard por garantia
    try {
      navigator.clipboard.writeText(fullMessage);
    } catch {
      // ignore
    }

    const waUrl = `https://wa.me/5541991815093?text=${encodeURIComponent(fullMessage)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center min-h-screen gap-6 p-6 text-center text-zinc-900 select-none"
          style={{ backgroundColor: "#ffffff" }}
        >
          <img
            src={NOT_FOUND_ILLUSTRATION}
            alt="TicketHall"
            width={768}
            height={512}
            className="mx-auto h-auto w-full max-w-[200px] object-contain sm:max-w-[260px]"
          />

          <div className="space-y-2">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
              Algo deu errado
            </h2>
            <p className="text-zinc-600 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              Ocorreu um erro inesperado ao carregar esta página. Você pode recarregar ou acionar nosso suporte imediatamente.
            </p>
          </div>

          {this.state.error && (
            <div className="w-full max-w-lg p-4 bg-zinc-50 rounded-2xl border border-zinc-200/80 text-left overflow-hidden shadow-sm">
              <p className="text-xs font-mono text-red-600 break-words line-clamp-3">
                {this.state.error.message}
              </p>
              {this.state.errorInfo && (
                <p className="text-[10px] font-mono text-zinc-400 mt-2 line-clamp-2">
                  {this.state.errorInfo.componentStack.split("\n")[1]}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            {/* Botão Fale com o Suporte */}
            <Button
              type="button"
              onClick={this.handleContactSupport}
              className="gap-2 bg-[#25D366] hover:bg-[#20ba5a] text-white border-0 rounded-full h-11 px-6 font-medium shadow-sm transition-transform active:scale-[0.98]"
            >
              <WhatsAppIcon className="w-4 h-4 fill-current" />
              Fale com o Suporte
            </Button>

            {/* Botão Recarregar página */}
            <Button
              type="button"
              onClick={() => window.location.reload()}
              className="gap-2 rounded-full h-11 px-6 font-medium bg-zinc-900 hover:bg-zinc-800 text-white shadow-sm transition-transform active:scale-[0.98]"
            >
              <RefreshCw className="w-4 h-4" />
              Recarregar página
            </Button>
            
            {/* Botão Copiar detalhes */}
            <Button
              type="button"
              variant="outline"
              onClick={this.handleCopyError}
              className="gap-2 rounded-full h-11 px-5 font-medium border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 transition-transform active:scale-[0.98]"
            >
              <Copy className="w-4 h-4" />
              Copiar detalhes
            </Button>
          </div>

          <p className="text-xs text-zinc-400 mt-2">
            Ao clicar em &quot;Fale com o Suporte&quot;, a rota, o horário e os detalhes do erro já são preenchidos prontos no WhatsApp.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}


